import { Controller } from "@hotwired/stimulus"
import { icon as brandIcon } from "../lib/icons"

// Connects to data-controller="sortable-billing-items"
export default class extends Controller {
  static targets = ["item"]
  static values = {
    templateId: Number,
    sortUrl: String
  }

  connect() {
    this.enableDragAndDrop()
    this.updateButtonStates()
    this.addKeyboardSupport()
  }

  itemTargetConnected() {
    this.updateButtonStates()
  }

  itemTargetDisconnected() {
    this.updateButtonStates()
  }

  moveUp(event) {
    event.preventDefault()
    const button = event.currentTarget
    const itemId = button.dataset.itemId

    if (!itemId) return

    // Show loading state
    button.disabled = true
    button.classList.add('loading')

    // Make the request
    fetch(`/items/${itemId}/move_up`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken(),
        'Accept': 'application/json'
      }
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          this.showNotification(data.error, 'error')
        } else {
          this.showNotification(data.message, 'success')
          this.reorderItems()
        }
      })
      .catch(error => {
        console.error('Error moving item up:', error)
        this.showNotification('Error al mover el concepto', 'error')
      })
      .finally(() => {
        button.disabled = false
        button.classList.remove('loading')
      })
  }

  moveDown(event) {
    event.preventDefault()
    const button = event.currentTarget
    const itemId = button.dataset.itemId

    if (!itemId) return

    // Show loading state
    button.disabled = true
    button.classList.add('loading')

    // Make the request
    fetch(`/items/${itemId}/move_down`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken(),
        'Accept': 'application/json'
      }
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          this.showNotification(data.error, 'error')
        } else {
          this.showNotification(data.message, 'success')
          this.reorderItems()
        }
      })
      .catch(error => {
        console.error('Error moving item down:', error)
        this.showNotification('Error al mover el concepto', 'error')
      })
      .finally(() => {
        button.disabled = false
        button.classList.remove('loading')
      })
  }

  reorderItems() {
    // Optimistically reorder items in the DOM based on current server state
    // This provides immediate visual feedback
    setTimeout(() => {
      window.location.reload()
    }, 500)
  }

  updateButtonStates() {
    this.itemTargets.forEach((item, index) => {
      const moveUpButton = item.querySelector('[data-action*="moveUp"]')
      const moveDownButton = item.querySelector('[data-action*="moveDown"]')

      if (moveUpButton) {
        if (index === 0) {
          moveUpButton.disabled = true
          moveUpButton.classList.add('btn-disabled')
        } else {
          moveUpButton.disabled = false
          moveUpButton.classList.remove('btn-disabled')
        }
      }

      if (moveDownButton) {
        if (index === this.itemTargets.length - 1) {
          moveDownButton.disabled = true
          moveDownButton.classList.add('btn-disabled')
        } else {
          moveDownButton.disabled = false
          moveDownButton.classList.remove('btn-disabled')
        }
      }
    })
  }

  enableDragAndDrop() {
    this.itemTargets.forEach(item => {
      item.addEventListener('dragstart', this.handleDragStart.bind(this))
      item.addEventListener('dragover', this.handleDragOver.bind(this))
      item.addEventListener('dragenter', this.handleDragEnter.bind(this))
      item.addEventListener('dragleave', this.handleDragLeave.bind(this))
      item.addEventListener('drop', this.handleDrop.bind(this))
      item.addEventListener('dragend', this.handleDragEnd.bind(this))

      // Add keyboard navigation attributes
      item.setAttribute('tabindex', '0')
      item.setAttribute('role', 'listitem')
      item.setAttribute('aria-grabbed', 'false')
    })
  }

  handleDragStart(event) {
    this.draggedElement = event.target.closest('[data-sortable-billing-items-target="item"]')
    this.draggedId = this.draggedElement.dataset.itemId

    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/html', this.draggedElement.outerHTML)

    // Visual feedback
    this.draggedElement.classList.add('opacity-50', 'transform', 'rotate-2', 'scale-105')
    this.draggedElement.setAttribute('aria-grabbed', 'true')

    // Add visual indicators to other items
    this.itemTargets.forEach(item => {
      if (item !== this.draggedElement) {
        item.classList.add('drag-target')
        item.setAttribute('aria-dropeffect', 'move')
      }
    })

    this.showNotification('Arrastrando concepto...', 'info', 1000)
  }

  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }

  handleDragEnter(event) {
    event.preventDefault()
    const item = event.target.closest('[data-sortable-billing-items-target="item"]')
    if (item && item !== this.draggedElement) {
      item.classList.add('drag-over')
    }
  }

  handleDragLeave(event) {
    const item = event.target.closest('[data-sortable-billing-items-target="item"]')
    if (item && !item.contains(event.relatedTarget)) {
      item.classList.remove('drag-over')
    }
  }

  handleDrop(event) {
    event.preventDefault()
    const dropTarget = event.target.closest('[data-sortable-billing-items-target="item"]')

    if (dropTarget && dropTarget !== this.draggedElement) {
      const container = dropTarget.parentNode
      const allItems = Array.from(container.children)
      const draggedIndex = allItems.indexOf(this.draggedElement)
      const dropIndex = allItems.indexOf(dropTarget)

      // Physically move the element in the DOM for immediate feedback
      if (draggedIndex < dropIndex) {
        container.insertBefore(this.draggedElement, dropTarget.nextSibling)
      } else {
        container.insertBefore(this.draggedElement, dropTarget)
      }

      // Save the new order to the server
      this.saveNewOrder()
    }

    // Clean up drag classes
    this.cleanupDragClasses()
  }

  handleDragEnd(event) {
    this.cleanupDragClasses()
    this.draggedElement = null
    this.draggedId = null
  }

  cleanupDragClasses() {
    this.itemTargets.forEach(item => {
      item.classList.remove('opacity-50', 'transform', 'rotate-2', 'scale-105', 'drag-target', 'drag-over')
      item.setAttribute('aria-grabbed', 'false')
      item.removeAttribute('aria-dropeffect')
    })
  }

  saveNewOrder() {
    const itemIds = this.itemTargets.map(item => item.dataset.itemId)

    // Show loading state
    this.showNotification('Guardando nuevo orden...', 'info')

    fetch(this.sortUrlValue, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken(),
        'Accept': 'application/json'
      },
      body: JSON.stringify({ item_ids: itemIds })
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then(data => {
        if (data.error) {
          this.showNotification(data.error, 'error')
          window.location.reload() // Reload to restore original order
        } else {
          this.showNotification(data.message, 'success')
          this.updateButtonStates()
          this.updatePositionLabels()
        }
      })
      .catch(error => {
        console.error('Error saving new order:', error)
        this.showNotification('Error al guardar el nuevo orden', 'error')
        window.location.reload() // Reload to restore original order
      })
  }

  updatePositionLabels() {
    // Update position labels after successful reorder
    this.itemTargets.forEach((item, index) => {
      const positionLabel = item.querySelector('.text-xs.font-mono')
      if (positionLabel) {
        positionLabel.textContent = `${index + 1}`
      }

      // Update aria-label for accessibility
      const serviceName = item.querySelector('h3.font-semibold')?.textContent || 'Concepto'
      item.setAttribute('aria-label', `${serviceName} - Posición ${index + 1} de ${this.itemTargets.length}`)
    })
  }

  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.getAttribute('content') : ''
  }

  showNotification(message, type = 'info', duration = 3000) {
    // Remove any existing notifications
    const existingToasts = document.querySelectorAll('.toast-notification')
    existingToasts.forEach(toast => toast.remove())

    // Create a toast notification
    const toast = document.createElement('div')
    toast.className = `toast-notification alert alert-${type} fixed top-4 right-4 w-auto max-w-sm z-50 shadow-lg transition-all duration-300 transform translate-x-full`

    let icon = ''
    switch (type) {
      case 'success':
        icon = brandIcon("check", "w-5 h-5")
        break
      case 'error':
        icon = brandIcon("x", "w-5 h-5")
        break
      case 'info':
      default:
        icon = brandIcon("info", "w-5 h-5")
        break
    }

    toast.innerHTML = `
      <div class="flex items-center">
        ${icon}
        <span class="ml-2">${message}</span>
      </div>
    `

    document.body.appendChild(toast)

    // Animate in
    requestAnimationFrame(() => {
      toast.classList.remove('translate-x-full')
    })

    // Remove after 3 seconds
    setTimeout(() => {
      if (toast.parentNode) {
        toast.classList.add('translate-x-full')
        setTimeout(() => toast.remove(), 300)
      }
    }, duration || 3000)
  }

  addKeyboardSupport() {
    this.itemTargets.forEach((item, index) => {
      item.addEventListener('keydown', (event) => {
        const itemId = item.dataset.itemId

        switch (event.key) {
          case 'ArrowUp':
            if (event.ctrlKey || event.metaKey) {
              event.preventDefault()
              this.moveItemUp(itemId)
            }
            break
          case 'ArrowDown':
            if (event.ctrlKey || event.metaKey) {
              event.preventDefault()
              this.moveItemDown(itemId)
            }
            break
          case 'Home':
            if (event.ctrlKey || event.metaKey) {
              event.preventDefault()
              this.moveItemToTop(itemId)
            }
            break
          case 'End':
            if (event.ctrlKey || event.metaKey) {
              event.preventDefault()
              this.moveItemToBottom(itemId)
            }
            break
          case 'Enter':
          case ' ':
            event.preventDefault()
            this.showKeyboardHelp()
            break
        }
      })
    })
  }

  moveItemUp(itemId) {
    const button = document.querySelector(`[data-action*="moveUp"][data-item-id="${itemId}"]`)
    if (button && !button.disabled) {
      button.click()
    }
  }

  moveItemDown(itemId) {
    const button = document.querySelector(`[data-action*="moveDown"][data-item-id="${itemId}"]`)
    if (button && !button.disabled) {
      button.click()
    }
  }

  moveItemToTop(itemId) {
    const currentIndex = this.itemTargets.findIndex(item => item.dataset.itemId === itemId)
    if (currentIndex > 0) {
      const newOrder = this.itemTargets.map(item => item.dataset.itemId)
      const [movedItem] = newOrder.splice(currentIndex, 1)
      newOrder.unshift(movedItem)

      this.saveCustomOrder(newOrder)
    }
  }

  moveItemToBottom(itemId) {
    const currentIndex = this.itemTargets.findIndex(item => item.dataset.itemId === itemId)
    if (currentIndex < this.itemTargets.length - 1) {
      const newOrder = this.itemTargets.map(item => item.dataset.itemId)
      const [movedItem] = newOrder.splice(currentIndex, 1)
      newOrder.push(movedItem)

      this.saveCustomOrder(newOrder)
    }
  }

  saveCustomOrder(itemIds) {
    fetch(this.sortUrlValue, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken(),
        'Accept': 'application/json'
      },
      body: JSON.stringify({ item_ids: itemIds })
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          this.showNotification(data.error, 'error')
          window.location.reload()
        } else {
          this.showNotification(data.message, 'success')
          setTimeout(() => window.location.reload(), 500)
        }
      })
      .catch(error => {
        console.error('Error saving custom order:', error)
        this.showNotification('Error al guardar el nuevo orden', 'error')
        window.location.reload()
      })
  }

  showKeyboardHelp() {
    this.showNotification(
      'Atajos de teclado: Ctrl+↑/↓ (mover), Ctrl+Home/End (inicio/final), Enter/Espacio (ayuda)',
      'info',
      5000
    )
  }
}
